import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import {
  ShieldCheck, ShieldAlert, Shield, RefreshCw, Lock, Unlock, Users, LogIn,
  AlertTriangle, Clock, Activity,
} from "lucide-react";
import { api } from "@/lib/api";

/**
 * Admin-only Güvenlik Sağlık Kartı.
 *
 * Shows at a glance:
 *   - Status pill (ok | attention | warning)
 *   - 24h failed/successful logins + active sessions
 *   - Locked accounts (and when they unlock)
 *   - Locked payroll months (short list)
 *   - Recent destructive audit events
 *   - Last security event timestamp
 */
export default function SecurityHealthCard() {
  const [data, setData] = useState(null);
  const [trend, setTrend] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [unlocking, setUnlocking] = useState({});

  const load = useCallback(async () => {
    setRefreshing(true);
    try {
      const [r, t] = await Promise.all([
        api.get("/security/health-card"),
        api.get("/security/login-trend?days=7"),
      ]);
      setData(r.data);
      setTrend(t.data);
    } catch (e) {
      toast.error("Güvenlik verileri yüklenemedi");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleUnlock = async (identifier) => {
    if (!window.confirm(`"${identifier}" hesabının kilidi kaldırılsın mı?\n\nBu, hesabın kilit sayacını sıfırlar ve kullanıcı hemen giriş deneyebilir.`)) return;
    setUnlocking((s) => ({ ...s, [identifier]: true }));
    try {
      const r = await api.post("/security/unlock-account", { identifier });
      if (r.data?.cleared) {
        toast.success(`Kilit kaldırıldı: ${identifier}`);
      } else {
        toast.info(r.data?.detail || "Kilit zaten yoktu");
      }
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kilit kaldırılamadı");
    } finally {
      setUnlocking((s) => {
        const n = { ...s };
        delete n[identifier];
        return n;
      });
    }
  };

  if (loading) {
    return (
      <div
        className="bg-slate-50 border border-slate-200 rounded-lg p-4 text-sm text-slate-400 text-center"
        data-testid="security-health-card-loading"
      >
        Güvenlik verileri yükleniyor...
      </div>
    );
  }
  if (!data) return null;

  const status = data.status || "ok";
  const statusMeta = {
    ok: { label: "Normal", bg: "bg-emerald-100", text: "text-emerald-800", border: "border-emerald-300", Icon: ShieldCheck, dot: "bg-emerald-500" },
    attention: { label: "İzle", bg: "bg-amber-100", text: "text-amber-800", border: "border-amber-300", Icon: Shield, dot: "bg-amber-500" },
    warning: { label: "Dikkat", bg: "bg-rose-100", text: "text-rose-800", border: "border-rose-300", Icon: ShieldAlert, dot: "bg-rose-500" },
  }[status] || null;
  const SIcon = statusMeta?.Icon || Shield;

  const fmtDate = (iso) => {
    if (!iso) return "—";
    try {
      const d = new Date(iso);
      return d.toLocaleString("tr-TR", { dateStyle: "short", timeStyle: "short" });
    } catch { return iso; }
  };

  return (
    <div
      className="bg-slate-50 border border-slate-200 rounded-lg p-4"
      data-testid="security-health-card"
    >
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <SIcon size={18} className={statusMeta?.text} />
          <h4 className="font-semibold text-sm text-slate-900">Güvenlik Sağlık Kartı</h4>
          <span
            data-testid="security-status-pill"
            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold border ${statusMeta?.bg} ${statusMeta?.text} ${statusMeta?.border}`}
          >
            <span className={`w-1.5 h-1.5 rounded-full ${statusMeta?.dot}`} />
            {statusMeta?.label}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[10px] text-slate-500" title={data.generated_at}>
            <Clock size={10} className="inline mr-1" />
            {fmtDate(data.generated_at)}
          </span>
          <button
            data-testid="security-refresh-btn"
            onClick={load}
            disabled={refreshing}
            className="inline-flex items-center gap-1 bg-slate-900 hover:bg-slate-800 text-white px-2 py-1 rounded text-[11px] font-semibold disabled:opacity-50"
          >
            <RefreshCw size={11} className={refreshing ? "animate-spin" : ""} /> Yenile
          </button>
        </div>
      </div>

      {/* KPI grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3">
        <StatCard
          icon={LogIn}
          label="Son 24h Başarılı Giriş"
          value={data.successful_logins_24h}
          tone="emerald"
          testid="kpi-success"
        />
        <StatCard
          icon={AlertTriangle}
          label="Son 24h Başarısız Giriş"
          value={data.failed_logins_24h}
          tone={data.failed_logins_24h > 10 ? "rose" : data.failed_logins_24h > 0 ? "amber" : "slate"}
          testid="kpi-failed"
        />
        <StatCard
          icon={Users}
          label="Aktif Oturum (24h)"
          value={`${data.active_sessions_24h}/${data.total_active_users}`}
          tone="indigo"
          testid="kpi-active"
          raw
        />
        <StatCard
          icon={Lock}
          label="Kilitli Hesap / Ay"
          value={`${data.locked_accounts}/${data.locked_months_count}`}
          tone={data.locked_accounts > 0 ? "amber" : "slate"}
          testid="kpi-locked"
          raw
        />
      </div>

      {/* 7-day login trend bar chart */}
      {trend && trend.rows && trend.rows.length > 0 && (
        <TrendChart trend={trend} />
      )}

      {/* Locked accounts */}
      {data.locked_accounts > 0 && (
        <div className="bg-white border border-amber-200 rounded-md p-3 mb-3" data-testid="security-locked-accounts">
          <div className="text-[11px] font-semibold uppercase tracking-wider text-amber-800 mb-1.5 flex items-center gap-1">
            <Lock size={11} /> Kilitli Hesaplar
          </div>
          <ul className="space-y-1 text-xs">
            {(data.locked_account_list || []).map((a) => (
              <li
                key={a.identifier}
                className="flex items-center justify-between gap-2 text-slate-700 py-0.5"
                data-testid={`locked-row-${a.identifier}`}
              >
                <span className="font-mono-tab truncate flex-1">{a.identifier}</span>
                <span className="text-slate-500 whitespace-nowrap text-[10px]">
                  {a.fails} başarısız · {fmtDate(a.locked_until)}
                </span>
                <button
                  type="button"
                  data-testid={`unlock-btn-${a.identifier}`}
                  onClick={() => handleUnlock(a.identifier)}
                  disabled={!!unlocking[a.identifier]}
                  className="inline-flex items-center gap-1 bg-emerald-600 hover:bg-emerald-700 text-white px-2 py-0.5 rounded text-[10px] font-semibold disabled:opacity-50"
                  title="Hesabın kilidini kaldır (login_attempts sıfırlanır)"
                >
                  <Unlock size={10} />
                  {unlocking[a.identifier] ? "..." : "Kilidi Kaldır"}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Locked payroll months */}
      {data.locked_months_count > 0 && (
        <div className="bg-white border border-slate-200 rounded-md p-3 mb-3" data-testid="security-locked-months">
          <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-700 mb-1.5 flex items-center gap-1">
            <Lock size={11} /> Kilitli Bordro Ayları
          </div>
          <div className="flex flex-wrap gap-1.5">
            {(data.locked_months || []).map((m) => (
              <span
                key={`${m.yil}-${m.ay}`}
                className="inline-flex items-center gap-1 bg-slate-100 text-slate-700 text-[10px] font-semibold px-2 py-0.5 rounded-full border border-slate-200"
                title={`${m.yil}/${String(m.ay).padStart(2, "0")} — ${m.locked_by || "—"}`}
              >
                {m.yil}/{String(m.ay).padStart(2, "0")}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Recent critical audits */}
      {data.recent_critical_audits && data.recent_critical_audits.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-md overflow-hidden" data-testid="security-recent-audits">
          <div className="px-3 py-1.5 bg-slate-100 text-[11px] font-semibold uppercase tracking-wider text-slate-700 flex items-center gap-1">
            <AlertTriangle size={11} /> Son 24h Kritik Olaylar ({data.critical_audits_24h})
          </div>
          <table className="w-full text-xs">
            <tbody>
              {data.recent_critical_audits.map((ev, idx) => {
                const toneCls = ev.action === "delete" ? "text-rose-700" : "text-slate-700";
                return (
                  <tr key={idx} className="border-t border-slate-100 hover:bg-slate-50">
                    <td className="px-3 py-1.5 text-slate-500 font-mono-tab whitespace-nowrap">
                      {fmtDate(ev.created_at)}
                    </td>
                    <td className={`px-3 py-1.5 font-semibold ${toneCls} whitespace-nowrap`}>
                      {ev.action.toUpperCase()}
                    </td>
                    <td className="px-3 py-1.5 text-slate-600 whitespace-nowrap">
                      {ev.entity}
                    </td>
                    <td className="px-3 py-1.5 text-slate-700 truncate max-w-[220px]" title={ev.label}>
                      {ev.label || <span className="text-slate-400 italic">—</span>}
                    </td>
                    <td className="px-3 py-1.5 text-slate-500 text-right whitespace-nowrap">
                      {ev.actor_email}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {data.locked_accounts === 0 && data.locked_months_count === 0 && (!data.recent_critical_audits || data.recent_critical_audits.length === 0) && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-md p-2.5 text-[11px] text-emerald-800 flex items-center gap-1.5">
          <ShieldCheck size={13} /> Son 24 saatte dikkat çekici bir güvenlik olayı yok. Bordro ayları kilitsiz. Hesaplar sağlıklı.
        </div>
      )}

      {data.latest_security_event && (
        <p className="text-[10px] text-slate-400 mt-2 text-right">
          Son güvenlik olayı: {fmtDate(data.latest_security_event)}
        </p>
      )}
    </div>
  );
}

function StatCard({ icon: Icon, label, value, tone, testid, raw }) {
  const toneCls = {
    emerald: "border-emerald-200 bg-emerald-50 text-emerald-800",
    amber: "border-amber-200 bg-amber-50 text-amber-800",
    rose: "border-rose-200 bg-rose-50 text-rose-800",
    indigo: "border-indigo-200 bg-indigo-50 text-indigo-800",
    slate: "border-slate-200 bg-white text-slate-700",
  }[tone] || "border-slate-200 bg-white text-slate-700";
  return (
    <div
      data-testid={testid}
      className={`rounded-md border p-2 ${toneCls}`}
    >
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-semibold">
        <Icon size={11} /> {label}
      </div>
      <div className="text-xl font-bold mt-0.5 font-mono-tab">
        {raw ? value : Number(value || 0).toLocaleString("tr-TR")}
      </div>
    </div>
  );
}

function TrendChart({ trend }) {
  const rows = trend.rows || [];
  const maxVal = Math.max(1, ...rows.map((r) => Math.max(r.success || 0, r.failed || 0)));
  const totalS = trend.total_success || 0;
  const totalF = trend.total_failed || 0;

  const dayLabel = (iso) => {
    try {
      const d = new Date(iso + "T00:00:00");
      const dayName = d.toLocaleDateString("tr-TR", { weekday: "short" });
      const num = d.getDate();
      return { dayName, num };
    } catch {
      return { dayName: "", num: iso };
    }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-md p-3 mb-3" data-testid="security-trend-chart">
      <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
        <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-700 flex items-center gap-1">
          <Activity size={11} /> Son 7 Gün Giriş Trendi
        </div>
        <div className="flex items-center gap-3 text-[10px] font-semibold">
          <span className="inline-flex items-center gap-1 text-emerald-700">
            <span className="inline-block w-2.5 h-2.5 rounded-sm bg-emerald-500" />
            Başarılı · {totalS}
          </span>
          <span className="inline-flex items-center gap-1 text-rose-700">
            <span className="inline-block w-2.5 h-2.5 rounded-sm bg-rose-500" />
            Başarısız · {totalF}
          </span>
        </div>
      </div>
      <div className="flex items-end gap-1.5 h-28" data-testid="security-trend-bars">
        {rows.map((r, i) => {
          const sPct = Math.max(2, (Number(r.success || 0) / maxVal) * 100);
          const fPct = Math.max(0, (Number(r.failed || 0) / maxVal) * 100);
          const label = dayLabel(r.date);
          return (
            <div
              key={r.date}
              className="flex-1 flex flex-col items-center gap-1 group"
              data-testid={`security-trend-day-${r.date}`}
              title={`${r.date}: ${r.success} başarılı · ${r.failed} başarısız`}
            >
              <div className="flex items-end justify-center gap-0.5 w-full flex-1 relative">
                <div
                  className="w-full max-w-[14px] bg-emerald-500 group-hover:bg-emerald-600 rounded-t transition-all"
                  style={{ height: `${sPct}%` }}
                >
                  {Number(r.success || 0) > 0 && sPct > 25 && (
                    <div className="text-[8px] text-white text-center font-bold pt-0.5">{r.success}</div>
                  )}
                </div>
                <div
                  className={`w-full max-w-[14px] rounded-t transition-all ${Number(r.failed || 0) > 0 ? "bg-rose-500 group-hover:bg-rose-600" : "bg-slate-100"}`}
                  style={{ height: `${fPct || 2}%` }}
                >
                  {Number(r.failed || 0) > 0 && fPct > 25 && (
                    <div className="text-[8px] text-white text-center font-bold pt-0.5">{r.failed}</div>
                  )}
                </div>
              </div>
              <div className="text-[9px] text-slate-500 text-center leading-tight">
                <div>{label.dayName}</div>
                <div className="font-semibold text-slate-700">{label.num}</div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

